// @core/stores/subscription/SubscriptionPlanStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  CreatePlanRequest,
  SubscriptionPlan,
  UpdatePlanRequest,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionPlanStore {
  // List state
  items: SubscriptionPlan[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: SubscriptionPlan | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, SubscriptionPlan>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): SubscriptionPlan | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: SubscriptionPlan | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create a new subscription plan
   */
  async CreatePlan(data: CreatePlanRequest): Promise<SubscriptionPlan> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.CreatePlan(data);
      const item = response.plan;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete subscription plan
   */
  async DeletePlan(plan_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeletePlan(plan_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== plan_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === plan_id) {
          this.currentItem = null;
        }

        this.removeFromCache(plan_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get subscription plan details
   */
  async GetPlan(plan_id: string): Promise<SubscriptionPlan> {
    // Check cache first
    const cached = this.cache.get(plan_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetPlan(plan_id);
      const item = response.plan;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List subscription plans
   */
  async ListPlans(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListPlans(params);

      runInAction(() => {
        this.items = response.plans?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update subscription plan
   */
  async UpdatePlan(plan_id: string, data: UpdatePlanRequest): Promise<SubscriptionPlan> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.UpdatePlan(plan_id, data);
      const item = response.plan;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const subscriptionPlanStore = new SubscriptionPlanStore();
